import Input from '../orm/Input'
import Model from '../orm/Model'

import Attendance from './Attendance'
import Course from './Course'
import Device from './Device'
import Event from './Event'
import Excuse from './Excuse'
import Team from './Team'
import StudentCourse from './StudentCourse'
import StudentEvent from './StudentEvent'
import StudentTeam from './StudentTeam'

import { isEmpty } from '@/utilities'

export default class Student extends Model {

    static entity = 'students'

    static fields() {
        return {
            id: this.attr(null),
            uuid: this.string(''),
            first_name: this.string(''),
            last_name: this.string(''),
            token: this.attr(null),
            email: this.string(''),
            phone: this.attr(null),
            school_identifier: this.attr(null),
            at_risk: this.boolean(false),
            weekly_apa: this.number(0),
            semester_apa: this.number(0),
            deleted_at: this.attr(null),
            last_seen: this.attr(null),
            courses: this.belongsToMany(
                Course, 
                StudentCourse,
                'student_id', 
                'course_id'
            ),
            events: this.belongsToMany(
                Event, 
                StudentEvent, 
                'student_id', 
                'event_id'
            ),
            device_id: this.number(0),
            device: this.belongsTo(Device, 'device_id'),

            teams: this.belongsToMany(
                Team,
                StudentTeam,
                'student_id',
                'team_id'
            ),

            excuses: this.hasMany(Excuse, 'student_id'),
            
            attendance: this.hasMany(Attendance, 'student_id')
        }
    }

    static graphql = {
        all: {
            paginated: true,
            parameters: {
                page: 'Int',
                per_page: 'Int',
                sort_by: 'String',
                sort_dir: 'String',
                state: 'String',
                missing: 'Boolean',
                relation: 'String',
                relation_uuid: 'String'
            }
        },

        relation: {
            paginated: true,
            parameters: {
                page: 'Int',
                per_page: 'Int',
                sort_by: 'String',
                sort_dir: 'String',
                state: 'String',
                relation: 'String',
                relation_uuid: 'String'
            },
            exclude: [
                'token',
                'phone',
                'school_identifier',
                'last_seen',
                'device_id'
            ]
        },

        search: {
            paginated: true,
            parameters: {
                query: 'String',
                per_page: 'Int!',
                unscoped: 'Boolean',
                relation: 'String',
                relation_uuid: 'String'
            }
        },

        one: {
            type: 'student',
            parameters: {
                uuid: 'String!',
                courses: {
                    week: 'String',
                    attendance: {
                        week: 'String'
                    }
                },
                events: {
                    state: 'String'
                }
            },
            include: {
                courses: {
                    location: true,
                    attendance: {
                        overrides: {
                            user: true
                        },
                        coordinates: true
                    }
                },
                events: {
                    location: true,
                    attendance: {
                        overrides: {
                            user: true
                        },
                        coordinates: true
                    },
                    in_waitlist: true
                },
                device: true,
                teams: true,
                excuses: true
            },
            exclude: {
                events: [
                    'attending_count',
                    'waitlist_count'
                ]
            }
        },

        create: {
            type: 'createStudent',
            parameters: {
                input: new Input('StudentInput!', [
                    'first_name',
                    'last_name',
                    'email',
                    'phone',
                    'at_risk',
                    'school_identifier'
                ])
            }
        },

        update: {
            type: 'updateStudent',
            parameters: {
                uuid: 'String!',
                input: new Input('StudentInput!', [
                    'first_name',
                    'last_name',
                    'email',
                    'phone',
                    'at_risk',
                    'school_identifier'
                ])
            }
        },

        remove: {
            type: 'removeStudent',
            parameters: {
                id: 'Int!'
            }
        },

        restore: {
            type: 'restoreStudent',
            parameters: {
                id: 'Int!'
            }
        },

        resetToken: {
            type: 'resetStudentToken',
            parameters: {
                uuid: 'String!',
                send_email: 'Boolean'
            }
        }
    }

    static $one(params) {
        if (!params.week) {
            params.week = new Date()
        }
        if (!params.state) {
            params.state = 'active'
        }
        return this.$fetchOne(params)
    }

    static async $search(params) {
        if (isEmpty(params.query)) return
        if (isEmpty(params.per_page)) {
            params.per_page = 5
        }
        params.unscoped = true
        const { data } = await this.$fetch({
            graphql: 'search',
            variables: params
        })

        return data
    }
}